// Loading do GPS Drive (2026-10-04, refeito 2026-10-05) — substitui o
// círculo girando (Loader2).
//
// Ícone compacto: uma linha de estrada com o tracejado correndo e um carro
// 3D visto de lado andando sobre ela, rodas girando. Sem textos/logos. O
// carro é render 3D único do hatchback de models-3d (scripts/render-loader-car):
// carroceria sem rodas + uma roda separada (girada por CSS) + posição dos
// eixos em car-side.json. Total ~24 KB; animação só transform/opacity.
//
// - <GpsBootLoader>: carregamento inicial (sessão). NUNCA vira splash: fica
//   invisível nos primeiros ~400 ms (só CSS, vale já no HTML do SSR), então
//   um F5 com sessão/cache rápido não mostra nada. Se passar de 15 s, mostra
//   um botão discreto de tentar de novo.
// - <GpsInlineLoader>: carregamentos secundários (gates de permissão,
//   painéis). Mesmo ícone menor, só na área da página.
// - <RouteProgressBar>: barra fina no topo durante navegação interna.
//
// O componente some assim que os dados chegam, então não disputa CPU com o
// mapa de 2.800+ veículos. prefers-reduced-motion para as animações.
import { useEffect, useState, type CSSProperties } from "react";
import { readCachedBrandLogo } from "@/hooks/use-branding-theme";
import { useRouterState } from "@tanstack/react-router";
import { RotateCw } from "lucide-react";
import { cn } from "@/lib/utils";
import bodyUrl from "@/assets/loader/car-side-body.webp";
import wheelUrl from "@/assets/loader/car-side-wheel.webp";
import carMeta from "@/assets/loader/car-side.json";

// Base da roda em relação ao topo da carroceria (fração da altura dela):
// a estrada começa exatamente aí.
const WHEEL_BOTTOM = carMeta.wheels[0].y + (carMeta.diameter * carMeta.aspect) / 2;

/** Carro de lado andando numa linha de estrada. `carWidth` em px (CSS aceito). */
function DrivingCar({ carWidth }: { carWidth: string }) {
  return (
    <div className="gl-drive" style={{ ["--gl-cw" as string]: carWidth, ["--gl-ar" as string]: carMeta.aspect, ["--gl-wb" as string]: WHEEL_BOTTOM }} aria-hidden>
      <div className="gl-car2">
        <div className="gl-car2-shadow" />
        <img src={bodyUrl} alt="" draggable={false} decoding="async" className="gl-car2-body" />
        {carMeta.wheels.map((w, i) => (
          <img
            key={i}
            src={wheelUrl}
            alt=""
            draggable={false}
            decoding="async"
            className="gl-wheel"
            style={{ left: `${w.x * 100}%`, top: `${w.y * 100}%`, width: `${carMeta.diameter * 100}%` }}
          />
        ))}
        <span className="gl-head" />
        <span className="gl-tail2" />
      </div>
      <div className="gl-road2">
        <div className="gl-road2-dash" />
      </div>
    </div>
  );
}

export function GpsBootLoader({
  slowAfterMs = 15000,
  onRetry,
  className,
}: {
  /** Depois desse tempo mostra o botão de tentar de novo. */
  slowAfterMs?: number;
  onRetry?: () => void;
  className?: string;
  /** Mantido por compatibilidade — a cena não exibe textos. */
  messages?: string[];
}) {
  const [slow, setSlow] = useState(false);
  // Lido no efeito (não no render) pra não divergir do HTML do SSR.
  const [logo, setLogo] = useState<string | null>(null);
  useEffect(() => setLogo(readCachedBrandLogo()), []);
  useEffect(() => {
    const s = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(s);
  }, [slowAfterMs]);

  return (
    <div role="status" aria-live="polite" className={cn("gl-boot gl-reveal", className)}>
      <span className="sr-only">Carregando</span>
      {/* No lugar da Porsche/carrinho (pedido 2026-10-29): logotipo da
          empresa com um raio vermelho em degradê girando em volta. */}
      <div className="boot-logo login-glow" style={{ "--lg-c": "#dc2626", "--lg-c2": "#ff7a6b" } as CSSProperties}>
        {logo && <img src={logo} alt="" className="boot-logo-img" />}
      </div>
      {slow && (
        <button
          type="button"
          onClick={onRetry ?? (() => window.location.reload())}
          aria-label="Tentar novamente"
          title="Tentar novamente"
          className="gl-retry"
        >
          <RotateCw className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/** Indicador discreto para carregamentos secundários. Não ocupa a tela toda. */
export function GpsInlineLoader({ className, size = 120 }: { label?: string; className?: string; size?: number }) {
  return (
    <div role="status" aria-live="polite" className={cn("gl-reveal flex items-center justify-center p-10", className)}>
      <span className="sr-only">Carregando</span>
      <DrivingCar carWidth={`${size}px`} />
    </div>
  );
}

/** Erro com opção de tentar de novo. */
export function GpsLoadError({ message, onRetry, className }: { message?: string; onRetry: () => void; className?: string }) {
  return (
    <div role="alert" className={cn("flex flex-col items-center justify-center gap-3 p-10 text-center", className)}>
      <p className="text-sm font-medium text-foreground">{message ?? "Não foi possível carregar as informações."}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted"
      >
        <RotateCw className="h-4 w-4" /> Tentar novamente
      </button>
    </div>
  );
}

/** Barra fina no topo enquanto o router resolve uma navegação interna. */
export function RouteProgressBar() {
  const pending = useRouterState({ select: (s) => s.status === "pending" });
  return <div aria-hidden className={cn("gl-topbar", pending && "gl-topbar-on")} />;
}
